import { FC, useEffect } from 'react'
import {
    useGetReceiptByDocIdQuery,
    useUpdateReceiptMutation,
} from '../../../../../../../../../store/api/receipts-api'
import { Col, DatePicker, Divider, Form, Input, Row, Spin, Typography } from 'antd'
import { useForm } from 'antd/es/form/Form'
import getInitialValues from '../../../../helpers/get-initial-values'
import { EditingFormValues } from '../../types/editing-form-values'
import Supplier from './components/supplier'
import Status from './components/status'
import ReceiptPositionEdit from '../receipt-position-edit/receipt-position-edit'
import useErrorModal from '../../../../../../../../../hooks/api/use-error-modal'
import {
    ConvertRelationFields,
    EndpointQueryParameters,
} from '../../../../../../../../../types/strapi/strapi'
import Receipt from '../../../../../../../../../types/api/receipt'
import _ from 'lodash'
import dayjs from 'dayjs'
import { outputDateFormat } from '../../../../../../../../../constants/constants'

interface EditFormProps {
    documentId: string | undefined
    onSuccess: () => void
}

const EditForm: FC<EditFormProps> = ({ documentId }) => {
    const [form] = useForm<EditingFormValues>()
    const id = documentId ? documentId : ''
    const receiptQueryParams: EndpointQueryParameters = {
        populate: {
            supplier: true,
            receipt_positions: {
                populate: {
                    material: { populate: '*' },
                    initiator: true,
                    unit: true,
                    order_request: { populate: { initiator: true } },
                },
            },
        },
    }
    const { data, isLoading } = useGetReceiptByDocIdQuery([id, receiptQueryParams], {
        skip: !id,
    })
    const [updateReceipt, updateReceiptState] = useUpdateReceiptMutation()

    useErrorModal(updateReceiptState, 'Неудалось обновить поле')

    useEffect(() => {
        if (data) {
            const initialValues = getInitialValues(data?.data)
            form.setFieldsValue(initialValues)
        }
    }, [data, form])

    const onChange = _.debounce(() => {
        save()
    }, 600)

    const save = async () => {
        const value = form.getFieldsValue()
        const initialValues = data?.data
        const updateData: Partial<ConvertRelationFields<Receipt>> = {}

        if (value.supplier && initialValues?.supplier?.documentId !== value.supplier) {
            updateData.supplier = { set: [value.supplier] }
        }
        if (value.receiptStatus && initialValues?.receiptStatus !== value.receiptStatus) {
            updateData.receiptStatus = value.receiptStatus
        }
        if (value.receiptSum && initialValues?.sum !== value.receiptSum) {
            updateData.sum = value.receiptSum
        }
        if (
            value.estimatedDate &&
            initialValues &&
            !dayjs(initialValues.estimatedDate).isSame(value.estimatedDate, 'day')
        ) {
            updateData.estimatedDate = value.estimatedDate.toISOString()
        }

        if (Object.keys(updateData).length === 0) return

        await updateReceipt({
            docId: id,
            body: {
                data: updateData,
            },
        })
    }

    return (
        <Spin spinning={isLoading}>
            <Form form={form} layout={'vertical'}>
                <Typography.Text type={'secondary'}>
                    Изменения в шапке заказа сохраняются автоматически
                </Typography.Text>
                <Row gutter={16} style={{ marginTop: 8 }}>
                    <Col xs={24} sm={12} md={4}>
                        <Form.Item name={'number'} label={'Номер заказа'}>
                            <Input disabled />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Supplier onChange={onChange} />
                    </Col>
                    <Col xs={24} sm={12} md={5}>
                        <Status onChange={onChange} />
                    </Col>
                    <Col xs={24} sm={12} md={4}>
                        <Form.Item name={'createdAt'} label={'Дата заказа'}>
                            <Input disabled />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={5}>
                        <Form.Item name={'estimatedDate'} label={'Дата поставки'}>
                            <DatePicker
                                onChange={onChange}
                                format={outputDateFormat}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider orientation={'left'} style={{ margin: '8px 0 16px' }}>
                    Позиции заказа
                </Divider>

                <ReceiptPositionEdit
                    dataSource={data?.data.receipt_positions}
                    receiptId={id}
                    receiptQueryParams={receiptQueryParams}
                    receiptStatus={data?.data.receiptStatus}
                />
            </Form>
        </Spin>
    )
}

export default EditForm
